/**
 * Pure matching/display helpers for the impersonation user picker.
 */

export interface ImpersonateUser {
  uid: string
  email: string
  firstName?: string
  lastName?: string
  userName?: string
  displayName?: string
}

export interface UserMatch {
  /** The string the query matched, exactly as rendered in the list. */
  text: string
  score: number
  indices: number[]
}

export function fuzzyMatch(str: string, query: string): { score: number; indices: number[] } | null {
  const s = str.toLowerCase()
  const q = query.toLowerCase()
  let si = 0,
    qi = 0,
    score = 0
  const indices: number[] = []

  while (si < s.length && qi < q.length) {
    if (s[si] === q[qi]) {
      indices.push(si)
      score++
      qi++
    }
    si++
  }

  return qi === q.length ? { score, indices } : null
}

/**
 * The identity strings shown under the email, in display order: display name,
 * "First Last", username. Blanks are dropped, duplicates collapse
 * case-insensitively, and anything identical to the email is dropped too — the
 * email is already the primary line.
 */
export function secondaryFields(user: ImpersonateUser): string[] {
  const candidates = [user.displayName, [user.firstName, user.lastName].filter(Boolean).join(' '), user.userName]
  const seen = new Set<string>([user.email.trim().toLowerCase()])
  const fields: string[] = []
  for (const candidate of candidates) {
    const trimmed = candidate?.trim()
    if (!trimmed) continue
    const key = trimmed.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    fields.push(trimmed)
  }
  return fields
}

/**
 * Match the query against everything rendered for the user — the email first,
 * then the secondary fields — so the highlight always lands on a visible
 * string. Every fuzzy hit scores the same (score = query length), so the first
 * matching field wins.
 */
export function matchUser(user: ImpersonateUser, query: string): UserMatch | null {
  for (const text of [user.email, ...secondaryFields(user)]) {
    const result = fuzzyMatch(text, query)
    if (result) return { text, ...result }
  }
  return null
}
